/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
import { domBind, domTrigger } from '../../lib/dom-event';
import window from 'window-or-global';
import { default as getCustomToolbarComponents } from './custom-toolbar-components';
import { isDarkFeatureEnabled } from '../utils/dark-features-query-selector';
import { addTableToolbar } from './editor_plugin_src';
import * as colors from '@atlaskit/theme/colors';
// TODO: remove jquery from here and from webpack config once CONFDEV-52616 is done.
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';
import tableToolbarEvents from './table-toolbar-events';

const getTinyMceTableToolbar = () => {
	return {
		initContextToolbars() {
			const aui = window.aui;
			const editor = window.tinymce.activeEditor;
			const CustomToolbarComponents = getCustomToolbarComponents();

			const getLang = (localKey) => {
				const key = `tinymce.${localKey}`;

				let shortcutKey;
				if (window.tinymce.isMac) {
					shortcutKey = `${key}_shortcut_mac`;
				} else {
					shortcutKey = `${key}_shortcut_win`;
				}

				let title = editor.translate(key);
				const shortcut = editor.translate(shortcutKey, '');
				if (shortcut && shortcut !== shortcutKey) {
					title += ` (${shortcut})`;
				}
				return title;
			};

			const classPrefix = 'aui-icon aui-icon-small aui-iconfont-table-';
			const storageManager = window.AJS.storageManager('atlassian.editor', 'tables');

			const tableToolbar = {
				Buttons: [
					new CustomToolbarComponents.Group([
						new CustomToolbarComponents.Button({
							text: getLang('table.row_before_desc'),
							iconClass: `${classPrefix}row-up`,
							click() {
								window.tinymce.execCommand('mceTableInsertRowBefore', false, '');
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.row_after_desc'),
							iconClass: `${classPrefix}row-down`,
							click() {
								window.tinymce.execCommand('mceTableInsertRowAfter', false, '');
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.delete_row_desc'),
							iconClass: `${classPrefix}row-remove`,
							click() {
								window.tinymce.execCommand('mceTableDeleteRow', false, '');
							},
						}),
					]),

					new CustomToolbarComponents.Group([
						new CustomToolbarComponents.Button({
							text: getLang('table.cut_row_desc'),
							iconClass: `${classPrefix}cut-row`,
							click() {
								window.tinymce.execCommand('mceTableCutRow', false, '');
								editor.plugins.customtoolbar.enableToolbarButton('aui-iconfont-table-paste-row');
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.copy_row_desc'),
							iconClass: `${classPrefix}copy-row`,
							click() {
								window.tinymce.execCommand('mceTableCopyRow', false, '');
								editor.plugins.customtoolbar.enableToolbarButton('aui-iconfont-table-paste-row');
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.paste_row_before_desc'),
							iconClass: `${classPrefix}paste-row`,
							click() {
								window.tinymce.execCommand('mceTablePasteRowBefore', false, '');
							},
							disabled: !storageManager.doesContain('copied'),
						}),
					]),

					new CustomToolbarComponents.Group([
						new CustomToolbarComponents.Button({
							text: getLang('table.col_before_desc'),
							iconClass: `${classPrefix}col-left`,
							click() {
								window.tinymce.execCommand('mceTableInsertColBefore', false, '');
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.col_after_desc'),
							iconClass: `${classPrefix}col-right`,
							click() {
								window.tinymce.execCommand('mceTableInsertColAfter', false, '');
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.delete_col_desc'),
							iconClass: `${classPrefix}col-remove`,
							click() {
								window.tinymce.execCommand('mceTableDeleteCol', false, '');
							},
						}),
					]),

					new CustomToolbarComponents.Group([
						new CustomToolbarComponents.Button({
							text: getLang('table.merge_cells_desc'),
							iconClass: `${classPrefix}merge`,
							click() {
								window.tinymce.execCommand('mceTableMergeCells', false, '');
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.split_cells_desc'),
							iconClass: `${classPrefix}split`,
							click() {
								window.tinymce.execCommand('mceTableSplitCells', false, '');
							},
						}),
					]),

					new CustomToolbarComponents.Group([
						new CustomToolbarComponents.Button({
							text: getLang('table.row_highlight'),
							iconClass: `${classPrefix}header-row`,
							click() {
								window.tinymce.execCommand('confTableRowToggleHeading', false);
								window.tinymce.execCommand('mceTableUpdateNumberingCol', false);
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.col_highlight'),
							iconClass: `${classPrefix}header-column`,
							click() {
								window.tinymce.execCommand('confTableColumnToggleHeading', false);
							},
						}),
					]),

					new CustomToolbarComponents.SplitGroup(
						[
							new CustomToolbarComponents.Button({
								text: getLang('table.selection_highlight'),
								iconClass: `${classPrefix}bg`,
								click() {
									window.tinymce.execCommand('confTableSelectionToggleHighlight', false);
								},
							}),
						],
						{
							id: 'table-highlight-group',
							dropDownOptions: {
								id: 'table-highlight-colour',
								dropDownContent: highlightColourPicker(),
								alignment: 'left',
							},
							postProcess(group) {
								group.attr('data-highlight-colour', 'grey');
								group.addClass('highlight-grey');

								// Overlay for showing the current highlight colour
								const highlightIndicator = $('<span>&nbsp;</span>');
								highlightIndicator.addClass('highlight-indicator');

								group.find('.icon.aui-iconfont-table-bg').after(highlightIndicator);
							},
						},
					),

					new CustomToolbarComponents.Group([
						new CustomToolbarComponents.Button({
							text: getLang('table.del'),
							iconClass: `${classPrefix}remove`,
							click() {
								window.tinymce.execCommand('mceTableDelete', false, '');
							},
						}),
					]),
				],

				// takes an array of {type: tinymceEvent, shouldTrigger: function, callback: function, missed: function }
				Events: tableToolbarEvents,
			};

			let handlingNodeChange;

			const isChildOfTable = (node) => {
				const table = $(node).closest('table:not(.wysiwyg-macro)');
				return table.length;
			};

			const hasColOrRowSpan = (node) => {
				//not the most efficent of solutions
				//but the table plugin is rather, well basically i am not touching it.
				function parseSpan(node, type) {
					return parseInt(node.attr(type), 10);
				}

				return (
					node.filter(function () {
						const temp = $(this);
						return parseSpan(temp, 'rowspan') > 1 || parseSpan(temp, 'colspan') > 1;
					}).length >= 1
				);
			};

			const updateSplitButton = (node) => {
				const clickedCell = $(node).closest('td,th');
				const selectedCells = $('.mceSelected', clickedCell.closest('table'));

				if (hasColOrRowSpan(clickedCell.add(selectedCells))) {
					editor.plugins.customtoolbar.enableToolbarButton('aui-iconfont-table-split');
				} else {
					editor.plugins.customtoolbar.disableToolbarButton('aui-iconfont-table-split');
				}
			};

			const updateTableMode = (node) => {
				$.fn.updateTableModeInToolbar && $(node).closest('table').updateTableModeInToolbar();
			};

			/**
			 * Creates table control extra settings menu on the toolbar
			 *
			 * @param {Object[]} itemList - array of itemList with options
			 * @param {String} itemList[].text - text that will be displayed on the label
			 * @param {String} itemList[].id - checkbox input id
			 * @param {Boolean} [itemList[].isChecked=false] - item checked state
			 * @param {Boolean} [itemList[].isDisabled=false] - item disabled state
			 * @param {Function} [itemList[].onChange] - callback input value changes
			 * @param {Function} [itemList[].onMenuOpen] - callback triggered when dropdown is opened
			 * @param {Function} [itemList[].onMenuClose] - callback triggered when dropdown is closed
			 */
			const TableMoreSettings = (function () {
				const triggerButtonCachedTemplate = aui.buttons.button({
					id: 'table-more-settings-trigger',
					text: '',
					iconType: 'aui',
					iconClass: 'aui-icon-small aui-iconfont-configure',
					iconText: getLang('table.more.heading'),
					extraAttributes: {
						type: 'button',
						'data-aui-trigger': 'true',
						'aria-controls': 'table-more-settings',
						'data-tooltip': getLang('table.more.heading'),
					},
				});

				const tableMoreSettingsInlineDialogCachedTemplate = aui.inlineDialog2.inlineDialog2({
					alignment: 'bottom left',
					respondsTo: 'toggle',
					id: 'table-more-settings',
				});

				const tableMoreSettingsCachedTemplate = aui.buttons.buttons({
					content: triggerButtonCachedTemplate,
				});

				return function (itemList) {
					function renderDialogContents(itemList) {
						const $contentContainer = $('<div></div>');
						const $dialogHeader = $(`<h2>${getLang('table.more.heading')}</h2>`);
						$contentContainer.append($dialogHeader);
						// Add items to the dropdown and bind ther events
						itemList.forEach((item) => {
							const $checkboxOption = $(
								aui.form.field({
									type: 'checkbox',
									id: item.id,
									labelContent: item.text,
									isChecked: item.isChecked(),
									isDisabled: item.isDisabled,
								}),
							);

							$checkboxOption.on('change', item.onChange); // we use change event so that it will also capture changes when click the item label

							// add back to menu
							$contentContainer.append($checkboxOption);
						});

						return $contentContainer;
					}

					this.render = function () {
						const $tableMoreSettings = $(tableMoreSettingsCachedTemplate);
						const $tableMoreSettingsInlineDialog = $(tableMoreSettingsInlineDialogCachedTemplate);
						const tableMoreSettingsSkate = $tableMoreSettingsInlineDialog[0];

						const hideDropdown = () => {
							// skate methods are bound to the native html element
							//CONFDEV-42959
							tableMoreSettingsSkate.open = false; //AUI 5.9 InlineDialog
						};

						// CONFDEV-42661 Render the dialog content on every aui-show event being triggered.
						// It looks like during the initialization, AUI cloned the content objects we created and lost all event listeners bounds to these objects.
						// The solution is to recreate, rebind events and replace all dialog contents, every time the AUI Inline Dialog is shown.
						$tableMoreSettingsInlineDialog.on('aui-show', (event) => {
							$('.aui-inline-dialog-contents', event.target).html(renderDialogContents(itemList));
						});

						// add the inline dialog to the tableMoreSettings button container
						$tableMoreSettings.append($tableMoreSettingsInlineDialog);

						// add hide events
						$(window).on('resize', $.debounce(hideDropdown, 100)); // AUI-UPGRADE - when we update AUI we should check if the opened dropdown position keeps on the correct place
						domBind(
							window.document,
							'enableContextToolbarRow.Toolbar disableContextToolbarRow.Toolbar',
							hideDropdown,
						);

						return $tableMoreSettings;
					};
				};
			})();

			// Can"t follow the event delegation model, as NodeChange events don"t supply an
			// event parameter (only an element), so the type of the event cannot be determined.
			editor.on('NodeChange', (e) => {
				const el = e.element;

				// required, as show/removing of toolbar seems to fire another nodeChangeEvent
				// CONFDEV-3419: Parent node null check to prevent table toolbar from being removed when
				// callbacks occur with the document or body nodes (this may happen when multiple cells are selected).
				const body = $('body');
				const animate = !body.hasClass('no-tools') || body.hasClass('no-tools-toolbars-visible');
				if (!handlingNodeChange && el.parentNode != null && el.nodeName !== 'BODY') {
					handlingNodeChange = true;
					if (isChildOfTable(el)) {
						domTrigger(window.document, 'createContextToolbarRow.Toolbar', {
							buttons: tableToolbar.Buttons,
							topToolbar: true,
							animate,
						});
						domTrigger(window.document, 'enableContextToolbarRow.Toolbar', {
							id: 'default',
						});

						if (isDarkFeatureEnabled('confluence.table.resizable')) {
							updateTableMode(el);
						}
						updateSplitButton(el);
					} else {
						domTrigger(window.document, 'disableContextToolbarRow.Toolbar', {
							animate,
						});
					}
					handlingNodeChange = false;
				}
			});

			editor.on('Undo', () => {
				//seems the bookmark gets restored after the node change from setcontent is triggered. Odd.
				editor.nodeChanged();
			});

			function highlightColourPicker() {
				const colourPicker = $('<ul/>').addClass('table-highlight-picker');
				const colourClasses = [
					colors.R500,
					colors.Y500,
					colors.G500,
					colors.T500,
					colors.B500,
					colors.P500,
					'#000000',
					colors.R400,
					colors.Y400,
					colors.G400,
					colors.T400,
					colors.B400,
					colors.P400,
					colors.N800,
					colors.R300,
					colors.Y300,
					colors.G300,
					colors.T300,
					colors.B300,
					colors.P300,
					colors.N500,
					colors.R200,
					colors.Y200,
					colors.G200,
					colors.T200,
					colors.B200,
					colors.P200,
					colors.N100,
					colors.R100,
					colors.Y100,
					colors.G100,
					colors.T100,
					colors.B100,
					colors.P100,
					colors.N50,
					colors.R75,
					colors.Y75,
					colors.G75,
					colors.T75,
					colors.B75,
					colors.P75,
					colors.N20,
					colors.R50,
					colors.Y50,
					colors.G50,
					colors.T50,
					colors.B50,
					colors.P50,
					'clear',
				];

				$.each(colourClasses, (idx, colour) => {
					const li = $('<li/>');
					const link = $("<a href='#'></a>");
					colour = colour.toLowerCase();

					if (colour === 'clear') {
						link.addClass('remove-highlight').attr('data-remove-highlight', true);
					} else {
						link.addClass(`highlight-${colour}`).attr('data-highlight-colour', colour);
					}
					li.append(link);
					colourPicker.append(li);
				});

				return colourPicker;
			}

			function selectColour(e) {
				const $buttonLink = $(this);
				const colourWidget = $('#table-highlight-group');
				const currentHighlightColour = colourWidget.attr('data-highlight-colour');
				const currentHighlightRemoval = colourWidget.attr('data-remove-highlight');
				if (currentHighlightColour) {
					colourWidget.removeClass(`highlight-${currentHighlightColour}`);
					colourWidget.removeAttr('data-highlight-colour');
				}
				colourWidget.removeClass('remove-highlight');

				if (currentHighlightRemoval) {
					colourWidget.removeAttr('data-remove-highlight');
				}

				const highlightColour = $buttonLink.attr('data-highlight-colour');
				if (highlightColour) {
					colourWidget.addClass(`highlight-${highlightColour}`);
					colourWidget.attr('data-highlight-colour', highlightColour);
				}
				if ($buttonLink.attr('data-remove-highlight')) {
					colourWidget.addClass('remove-highlight');
					colourWidget.attr('data-remove-highlight', true);
				}

				editor.focus();
				editor.execCommand('confTableSelectionToggleHighlight', false, {
					alwaysHighlight: true,
				});
				e.preventDefault();
			}

			function tableModeList() {
				const $ul = $('<ul/>').addClass('table-mode-list');
				const tableModes = [
					{
						name: getLang('table.responsive'),
						mceCmd: 'mceAutoWidth',
						tooltip: getLang('table.responsive_tooltip'),
					},
					{
						name: getLang('table.fixed_width'),
						mceCmd: 'mceFixedWidth',
						tooltip: getLang('table.fixed_width_tooltip'),
					},
				];

				$.each(tableModes, (idx, mode) => {
					const $li = $('<li/>').addClass('dropdown-item').attr('data-tooltip', mode.tooltip);
					const $link = $(`<a href='#'>${mode.name}</a>`).addClass('item-link');
					$li.on('click', () => {
						window.tinymce.execCommand(mode.mceCmd, false, '');
					});

					$ul.append($li.append($link));
				});
				return $ul;
			}

			// Editor is already initialized at this stage
			if (editor.initialized) {
				$(window.document).delegate(
					'#table-highlight-colour .aui-dropdown a',
					'click',
					selectColour,
				);
			} else {
				editor.on('init', () => {
					$(window.document).delegate(
						'#table-highlight-colour .aui-dropdown a',
						'click',
						selectColour,
					);
				});
			}

			editor.on('Remove', () => {
				$(window.document).undelegate(
					'#table-highlight-colour .aui-dropdown a',
					'click',
					selectColour,
				);
			});

			if (isDarkFeatureEnabled('confluence.table.resizable')) {
				tableToolbar.Buttons.splice(
					0,
					0,
					new CustomToolbarComponents.Group([
						new CustomToolbarComponents.DropDownButton({
							id: 'table-mode-picker',
							text: getLang('table.auto_width'),
							dropDownContent: tableModeList(),
						}),
					]),
				);
			}

			if (!isDarkFeatureEnabled('confluence-table-enhancements.copy-cut-paste-cols.disabled')) {
				// We only add the buttons group in here because we need a way to dark-feature it
				tableToolbar.Buttons.splice(
					3,
					0,
					new CustomToolbarComponents.Group([
						new CustomToolbarComponents.Button({
							text: getLang('table.col_cut_desc'),
							iconClass: `${classPrefix}cut-column`,
							click() {
								window.tinymce.execCommand('mceTableCutCol', false, '');
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.col_copy_desc'),
							iconClass: `${classPrefix}copy-column`,
							click() {
								window.tinymce.execCommand('mceTableCopyCol', false, '');
							},
						}),

						new CustomToolbarComponents.Button({
							text: getLang('table.col_paste_desc'),
							iconClass: `${classPrefix}paste-column`,
							click() {
								window.tinymce.execCommand('mceTablePasteColBefore', false, '');
							},
						}),
					]),
				);
			}

			if (!isDarkFeatureEnabled('confluence-table-enhancements.auto-row.disabled')) {
				const moreSettingButtons = [];
				if (!isDarkFeatureEnabled('confluence-table-enhancements.auto-row.disabled')) {
					moreSettingButtons.push({
						text: getLang('table.numbering_column_desc'),
						id: 'insert-numbering-column',
						isChecked: () => {
							return (
								$(editor.selection.getNode()).closest('table').find('.numberingColumn').length > 0
							);
						},
						onChange: () => {
							window.tinymce.execCommand('mceTableInsertNumberingCol', false, '');
						},
					});
				}

				tableToolbar.Buttons.push(new TableMoreSettings(moreSettingButtons));
			}

			return tableToolbar;
		},
	};
};

export function initTableToolbar() {
	domBind(window.document, 'initContextToolbars.Toolbar', (e) => {
		getTinyMceTableToolbar().initContextToolbars(e);
		addTableToolbar();
	});
}
export function initTableToolbarWRM() {
	return [
		'wr!confluence.web.resources:ajs',
		// AUI-UPGRADE - remove when upgrading to aui 5.9 when require will no longer be needed
		'wr!com.atlassian.auiplugin:aui-inline-dialog2',
		'wr!confluence.web.resources:legacy-editor-global-AVOID-IF-POSSIBLE',
	];
}

export { getTinyMceTableToolbar as _getTinyMceTableToolbar };
